/**
 * Date Utility Functions
 * Handles date formatting and parsing without timezone conversion issues
 */

/**
 * Get today's date in YYYY-MM-DD format using local timezone
 * Avoids timezone shifts by using local date components
 */
export const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse any date string/object to YYYY-MM-DD format without timezone conversion
 * Handles:
 * - YYYY-MM-DD strings (returned as-is)
 * - ISO datetime strings (extracts date part)
 * - Date objects (extracts local date components)
 */
/**
 * Extract YYYY-MM-DD for display from API values without falling back to "today"
 * (parseDateString returns getTodayDate() on some failures, which caused wrong UI dates).
 */
export const extractDateYmdForDisplay = (raw: unknown): string | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const m = t.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s.]|$)/);
    if (m) return m[1];
    return null;
  }
  if (raw instanceof Date && !isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const mo = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${mo}-${d}`;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const mo = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${mo}-${d}`;
  }
  return null;
};

export const parseDateString = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return getTodayDate();
  
  // If it's already a Date object
  if (dateString instanceof Date) {
    if (isNaN(dateString.getTime())) return getTodayDate();
    const year = dateString.getFullYear();
    const month = String(dateString.getMonth() + 1).padStart(2, '0');
    const day = String(dateString.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // If it's a string
  const dateStr = String(dateString).trim();
  
  // If date is already in YYYY-MM-DD format, use it directly
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // If it's a MySQL datetime string (YYYY-MM-DD HH:MM:SS)
  // Always extract just the date part BEFORE creating Date object to avoid timezone issues
  const mysqlDateTimeMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})(\s+(\d{2}):(\d{2}):(\d{2}))?/);
  if (mysqlDateTimeMatch) {
    const datePart = mysqlDateTimeMatch[1];
    // Validate the date part format and return it directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }
  
  // If it's an ISO string (with T and time), extract just the date part
  if (dateStr.includes('T')) {
    const datePart = dateStr.split('T')[0];
    // Validate the date part format
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }
  
  // For pure date strings (YYYY-MM-DD), parse as local date to avoid timezone conversion
  // This ensures "2025-08-18" displays as Aug 18, not Aug 17
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Parse as local date components directly (not UTC) to preserve the date
    const [year, month, day] = dateStr.split('-');
    // Validate the components
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    if (yearNum >= 1900 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
      return dateStr; // Return as-is since it's already in the correct format
    }
  }
  
  // Otherwise, try to parse it as a Date object
  // But first check if it's an ISO string with timezone - extract date part before parsing
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
    // ISO datetime string - extract date part to avoid timezone conversion
    const datePart = dateStr.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }
  
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return getTodayDate();
  
  // Extract date components using local timezone to preserve the intended date
  // This prevents timezone shifts when displaying dates
  // IMPORTANT: Use local methods (not UTC) to preserve the date as intended
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date for backend (ensures YYYY-MM-DD format)
 */
export const formatDateForBackend = (dateString: string | Date | null | undefined): string => {
  return parseDateString(dateString);
};

/**
 * Format date for display (DD/MM/YYYY)
 */
export const formatDateForDisplay = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '';
  
  const parsedDate = parseDateString(dateString);
  if (!parsedDate) return '';
  
  const [year, month, day] = parsedDate.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Format date for display in locale format (e.g., "Aug 18, 2025")
 * Formats directly from date string components to avoid timezone conversion issues
 * NEVER creates Date objects to prevent timezone shifts
 */
export const formatDateLocale = (dateString: string | Date | null | undefined, locale: string = 'en-US'): string => {
  if (!dateString) return '';
  
  // Use extractDateYmdForDisplay — never fall back to today's date on parse failure
  let parsed = extractDateYmdForDisplay(dateString);
  if (!parsed) {
    const dateStr = String(dateString).trim();
    const dateMatch = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      parsed = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
    } else {
      return '';
    }
  }
  
  // Format directly from YYYY-MM-DD string to avoid any timezone conversion
  const [year, month, day] = parsed.split('-');
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  
  // Validate components (reject invalid/zero MySQL dates like 0000-00-00)
  if (
    isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum) ||
    yearNum < 1900 || yearNum > 2100 ||
    monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31
  ) {
    return '';
  }
  
  if (locale === 'en-US' || locale.startsWith('en')) {
    // Format as "Aug 18, 2025" directly from components (no Date object)
    // This completely avoids timezone conversion
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[monthNum - 1] || month;
    return `${monthName} ${dayNum}, ${year}`;
  } else {
    // For other locales, create a Date object but use local components
    // This is safe because we're using the parsed date components
    const date = new Date(parseInt(year), monthNum - 1, dayNum);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

/**
 * Format a date string (YYYY-MM-DD) for display using local date only.
 * Parses as local date components to avoid timezone shift (e.g. 2026-03-02 stays March 2 everywhere).
 */
export const formatDateLocal = (
  dateString: string | null | undefined,
  locale: string = 'fr-FR',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return '';
  const parsed = parseDateString(dateString);
  if (!parsed || !/^\d{4}-\d{2}-\d{2}$/.test(parsed)) return '';
  const [y, m, d] = parsed.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return '';
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, options ?? { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Get date from X days ago in YYYY-MM-DD format
 */
export const getDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return parseDateString(date);
};

/**
 * Get date from X days in the future in YYYY-MM-DD format
 */
export const getDateDaysFromNow = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return parseDateString(date);
};

/**
 * Check if a date is in the past
 */
export const isDateInPast = (dateString: string | Date | null | undefined): boolean => {
  if (!dateString) return false;
  const date = parseDateString(dateString);
  const today = getTodayDate();
  return date < today;
};

/**
 * Check if a date is today
 */
export const isDateToday = (dateString: string | Date | null | undefined): boolean => {
  if (!dateString) return false;
  // Prefer extractDateYmdForDisplay — never use parseDateString here
  // (it can fall back to getTodayDate() and falsely treat invalid dates as today).
  const ymd = extractDateYmdForDisplay(dateString);
  if (!ymd) return false;
  return ymd === getTodayDate();
};

/**
 * Whether a todo should appear in today's notification card (due date and/or reminder date).
 */
export const isTodoDueOrReminderToday = (todo: {
  due_date?: string | null;
  reminder_date?: string | null;
}): boolean => {
  return isDateToday(todo.due_date) || isDateToday(todo.reminder_date);
};

/**
 * Calculate age in years from date of birth, with optional stored age fallback.
 * Parses YYYY-MM-DD safely to avoid timezone-related NaN/invalid Date issues.
 */
export const calculateAgeFromDateOfBirth = (
  dateOfBirth: string | Date | null | undefined,
  storedAge?: number | string | null
): number | null => {
  const ymd = extractDateYmdForDisplay(dateOfBirth);
  if (ymd) {
    const [year, month, day] = ymd.split('-').map(Number);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      const today = new Date();
      let age = today.getFullYear() - year;
      const monthDiff = today.getMonth() + 1 - month;
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
        age--;
      }
      if (age >= 0 && age <= 150) {
        return age;
      }
    }
  }

  if (storedAge != null && storedAge !== '') {
    const parsed = typeof storedAge === 'number' ? storedAge : parseInt(String(storedAge), 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 150) {
      return parsed;
    }
  }

  return null;
};

/**
 * Calculate days between two dates
 */
export const daysBetween = (date1: string | Date, date2: string | Date): number => {
  const d1 = new Date(parseDateString(date1));
  const d2 = new Date(parseDateString(date2));
  const diffTime = d2.getTime() - d1.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};
